import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Customer } from '@inyova/models';

import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as ProfileActions from '@profile/profile.actions';
import * as fromProfile from '@profile/profile.reducers';

import { EUROPE_COUNTRIES } from '@app/app.constants';
import { extractNumberAndCountryCodeFromPhoneNumber, getExampleMobilePhoneNumberForCountry } from '@shared/utils';

@Component({
  selector: 'app-profile-mobile-phone',
  templateUrl: './profile-mobile-phone.component.html',
  styleUrls: ['./profile-mobile-phone.component.scss'],
  standalone: false
})
export class ProfileMobilePhoneComponent implements OnInit, OnDestroy, AfterViewInit {
  chCustomer: boolean;
  inputOptions: { [key: string]: string | boolean | string[] | ((customPlaceholder: string) => string) };
  initialMobilePhone: { number: string; countryCode: string };
  isPhoneCountryValid = true;
  mobilePhoneForm: FormGroup;
  mobileValidateForm: FormGroup;
  onlyCountries = EUROPE_COUNTRIES;
  phoneNumber: string | null = null;
  showValidationError = false;
  token: string;
  mobilePhonePlaceholder: string;

  loading$: Observable<boolean>;
  passwordValidated$: Observable<boolean>;
  validatePhone$: Observable<boolean>;
  onDestroy$: Subject<void> = new Subject<void>();

  @Input() customer: Customer;
  @Input() hasAccountMinProspect: boolean;
  @Input() has3AAccountMinProspect: boolean;
  @Input() twoFAEnabled: boolean;

  get mobilePhoneControl(): AbstractControl | null {
    return this.mobilePhoneForm.get('phone');
  }

  get mobileValidateControl(): AbstractControl | null {
    return this.mobileValidateForm.get('code');
  }

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // selectors
    this.loading$ = this.store.select(fromProfile.selectLoading);
    this.validatePhone$ = this.store.select(fromProfile.selectValidatePhone);
    this.passwordValidated$ = this.store.select(fromProfile.selectPasswordValidated);
    this.store
      .select(fromProfile.selectValidatePhoneError)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((validatePhoneError: boolean) => {
        if (validatePhoneError) {
          this.mobileValidateControl.setValue('');
          this.showValidationError = true;
        }
      });
    // set props
    this.chCustomer = this.customer.app_location === 'ch';

    // create forms
    this.mobilePhoneForm = this.createMobilePhoneForm();
    this.mobileValidateForm = this.createMobileValidateForm();

    // listen to the otp code change
    this.mobileValidateControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.onDestroy$)).subscribe((code: string) => {
      this.showValidationError = false;
      if (!code || code.length !== 6) {
        return;
      }
      this.onValidateMobilePhone(code);
    });

    this.initialMobilePhone = extractNumberAndCountryCodeFromPhoneNumber(
      this.has3AAccountMinProspect
        ? this.customer.verified_phone || this.customer.unverified_phone
        : this.customer.unverified_phone || this.customer.verified_phone
    );

    if (
      this.chCustomer &&
      this.initialMobilePhone !== null &&
      this.initialMobilePhone.countryCode.toLocaleLowerCase() !== this.customer.app_location
    ) {
      this.initialMobilePhone = { number: '', countryCode: this.customer.app_location.toUpperCase() };
    }

    this.mobilePhoneControl.setValue(this.initialMobilePhone);

    this.inputOptions = {
      initialCountry: this.initialMobilePhone?.countryCode ?? this.customer.app_location,
      separateDialCode: true,
      preferredCountries: !this.chCustomer ? ['de'] : [],
      onlyCountries: !this.chCustomer ? this.onlyCountries : ['ch']
    };

    // disable input for those two categories
    if (this.customer.provider === 'email' || !this.hasAccountMinProspect) {
      return;
    }
    this.mobilePhoneControl.disable();
  }

  ngAfterViewInit() {
    this.updateMobilePhonePlaceholder(this.initialMobilePhone?.countryCode ?? this.customer.app_location);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.store.dispatch(ProfileActions.validatePasswordReset());
    this.store.dispatch(ProfileActions.sendOtpCodeReset());
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  createMobilePhoneForm(): FormGroup {
    return this.fb.group({
      phone: ['', [Validators.required]]
    });
  }

  createMobileValidateForm(): FormGroup {
    return this.fb.group({
      code: ['', [Validators.required]]
    });
  }

  onValidatePassword(password: string) {
    this.store.dispatch(ProfileActions.validatePassword({ password }));
  }

  onUpdateMobilePhone(): void {
    if (this.mobilePhoneForm.invalid) {
      return;
    }

    this.phoneNumber = this.mobilePhoneControl.getRawValue().e164Number;

    if (this.initialMobilePhone?.number === this.phoneNumber) {
      this.store.dispatch(ProfileActions.updatePersonalDetailsCurrentScreen({ screen: 'main' }));
      return;
    }

    this.store.dispatch(
      ProfileActions.updateProfileMobilePhone({
        personalDetails: { phone: this.phoneNumber },
        has3AAccountMinProspect: this.has3AAccountMinProspect,
        twoFAEnabled: this.twoFAEnabled
      })
    );
  }

  onValidateMobilePhone(code: string) {
    this.store.dispatch(ProfileActions.validateMobilePhone({ code }));
  }

  updateMobilePhonePlaceholder(countryCode: string) {
    this.mobilePhonePlaceholder = getExampleMobilePhoneNumberForCountry(countryCode);
  }

  onCountryChange(event: { iso2: string }): void {
    this.updateMobilePhonePlaceholder(event.iso2);
  }

  onResendCode() {
    this.showValidationError = false;
    this.mobileValidateControl.setValue('');
    this.mobileValidateControl.updateValueAndValidity();
    this.store.dispatch(ProfileActions.sendOtpCode());
  }

  resetOtpValidation() {
    this.store.dispatch(ProfileActions.sendOtpCodeReset());
  }

  onCancel() {
    this.store.dispatch(ProfileActions.updatePersonalDetailsCurrentScreen({ screen: 'main' }));
  }
}
